import React, { useState } from "react";
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Input,
  Alert,
  Dialog,
} from "@mui/material";
import { appConfig } from "../../config/appConfig";
import { decode } from "../../utils/helper";
import { getStorageItemLocal } from "../../services/storageServices";

interface Props {
  onClose: () => void;
  open: boolean;
  selectedFile: any;
  setSelectedFile: any;
}

const MAX_FILE_SIZE_MB = 100;
const ALLOWED_TYPE = "video/mp4";

const VideoUploadDialogBox: React.FC<Props> = ({
  onClose,
  open,
  setSelectedFile,
  selectedFile,
}) => {
  const [error, setError] = useState<string | null>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (file) {
      if (file.type !== ALLOWED_TYPE) {
        setError("Only .mp4 video files are allowed.");
        setSelectedFile(null);
        return;
      }

      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        setError("File size must be less than or equal to 100MB.");
        setSelectedFile(null);
        return;
      }

      setSelectedFile(file);
      setError(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || error) {
      setError("Please select a valid .mp4 video file.");
      return;
    }
    const username = getStorageItemLocal("username") ?? "";

    const payload = new FormData();
    payload.append("UploadBy", JSON.stringify(username));
    payload.append("Status", "unapprove");
    payload.append("Publish", "false");

    payload.append("VideoFiles", selectedFile);

    try {
      const response = await fetch(
        appConfig.serviceUrls.uploadIntroductoryVideo,
        {
          method: "POST",
          body: payload,
        }
      );
      if (!response.ok) {
        alert("Interwork Error");
        throw new Error("Upload failed");
      }
      alert("Upload successful!");

      onClose();
    } catch (err) {
      console.error(err);
      setError("Failed to upload. Please try again.");
    }

    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      className="dialog-component"
    >
      <DialogTitle>Upload Video</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2}>
          <Typography>Select a .mp4 video file (max 100MB):</Typography>
          <Input
            type="file"
            inputProps={{ accept: "video/mp4" }}
            onChange={handleFileChange}
          />
          {error && <Alert severity="error">{error}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleUpload}
          variant="contained"
          disabled={!selectedFile}
        >
          Upload
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VideoUploadDialogBox;
